import { c as _c } from "react/compiler-runtime";
import { formatDistanceToNow } from 'date-fns';
import Image from 'next/image';
import { StarRating } from '@/components/StarRating';
import { GradientCard, type GradientCardProps } from '@/components/cards/GradientCard';
import { breakpoints } from '@/lib/breakpoints';
import { cn } from '@/lib/cn';
import { isValidDate } from '@/lib/dates';
import type { CmsTestimonial } from '@/services/cms-sdk';
const emptyTestimonial: Partial<CmsTestimonial> = {
  comment: 'No comment provided'
};
export interface TestimonialCardProps extends GradientCardProps {
  testimonial?: CmsTestimonial;
  variant?: 'simple' | 'image';
}
export function TestimonialCard(t0) {
  const $ = _c(43);
  let children;
  let className;
  let props;
  let t1;
  let testimonialIn;
  if ($[0] !== t0) {
    ({
      testimonial: testimonialIn,
      className,
      children,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = testimonialIn;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    testimonialIn = $[5];
  }
  const variant = t1 === undefined ? "simple" : t1;
  const testimonial = testimonialIn || emptyTestimonial;
  let t2;
  if ($[6] !== testimonial.date) {
    t2 = isValidDate(testimonial.date) ? formatDistanceToNow(testimonial.date) : null;
    $[6] = testimonial.date;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const timeAgo = t2;
  const t3 = variant === "simple";
  const t4 = variant === "image";
  let t5;
  if ($[8] !== className || $[9] !== t3 || $[10] !== t4) {
    t5 = cn("flex flex-col", {
      "items-center justify-between md:justify-center px-10 py-10 md:px-16 md:py-12": t3,
      "md:grid md:grid-cols-2 gap-3 md:gap-2 auto-rows-auto pt-10 pl-10 md:py-16 md:pl-17 pr-0 overflow-hidden": t4
    }, className);
    $[8] = className;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `testimonial-card-${testimonialIn?.id}`;
  let t7;
  if ($[12] !== testimonial.rating) {
    t7 = testimonial.rating ? <StarRating data-testid="testimonial-rating" value={testimonial.rating} className={cn("md:text-2xl items-end", {})} /> : null;
    $[12] = testimonial.rating;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = variant === "simple";
  const t9 = variant === "image";
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = cn("text-lg md:text-[32px] leading-[29px] md:leading-[51px] content-center min-h-29 max-h-37 p-0 md:min-h-39 md:max-h-auto md:my-2 md:max-h-auto overflow-auto font-bolden-van", {
      "text-center": t8,
      "mr-6 md:m-0 col-start-1 row-start-2 items-center": t9
    });
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = testimonial.comment || emptyTestimonial.comment;
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <p data-testid="testimonial-comment" className={t10}>“{t11}”</p>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = variant === "simple";
  const t14 = variant === "image";
  let t15;
  if ($[20] !== t13 || $[21] !== t14) {
    t15 = cn("text-sm md:text-lg leading-[22px] md:leading-[29px]", {
      "text-center": t13,
      "col-start-1 row-start-3 items-start": t14
    });
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = testimonial.author || "Anonymous";
  let t17;
  if ($[23] !== t16) {
    t17 = <span data-testid="testimonial-author">{t16}</span>;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== timeAgo) {
    t18 = timeAgo ? <>{" "}– <span data-testid="testimonial-date">{timeAgo} ago</span></> : null;
    $[25] = timeAgo;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t15 || $[28] !== t17 || $[29] !== t18) {
    t19 = <div className={t15}>{t17}{t18}</div>;
    $[27] = t15;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== testimonial.fleetCategory || $[32] !== variant) {
    t20 = variant === "image" && testimonial.fleetCategory?.image ? <div className="row-span-3 col-start-2 row-start-1 overflow-hidden content-center relative"><div className="w-full h-29 md:h-66 relative"><Image draggable={false} src={testimonial.fleetCategory.image} alt={testimonial.fleetCategory.name || ""} fill={true} sizes={`(max-width: ${breakpoints.sm}) 300px, 768px`} className="max-w-none md:object-cover object-[center_left] w-full translate-x-1/2 md:translate-x-17/100" /></div></div> : null;
    $[31] = testimonial.fleetCategory;
    $[32] = variant;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== children || $[35] !== props || $[36] !== t12 || $[37] !== t19 || $[38] !== t20 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7) {
    t21 = <GradientCard className={t5} data-testid={t6} {...props}>{t7}{t12}{t19}{t20}{children}</GradientCard>;
    $[34] = children;
    $[35] = props;
    $[36] = t12;
    $[37] = t19;
    $[38] = t20;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
}